<script lang="ts">
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import {
    Flex,
    GlobalSearch,
    NavLink,
    Text,
    highlightedSuggestionIndexStore,
  } from "@getprovi/craft";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { beforeUrlChange, goto, url } from "@roxi/routify";
  import { debounce, trim } from "lodash";
  import { getSuggestions } from "@retailer/services/search/suggestions_service";
  import { suggestionRouting } from "@retailer/services/search/suggestions_routing";
  import { SearchEntity, SearchSuggestionEntity } from "@shared/services/event_tracking/entities";

  import type { Suggestion, SuggestionGroup } from "@shared/types/Suggestion";

  const { trackClick, trackEvent } = EventTracking;

  let isSuggestionBoxOpen: boolean = false;
  let searchText: string = "";
  let groupedSuggestions: SuggestionGroup[] = [];
  let suggestions: Suggestion[] = [];

  $: placeholder = $LayoutStore.isMobile ? "Search" : "Search products, brands or distributors";

  const buildHrefOrRedirect = (suggestion: Suggestion, redirect: boolean = false): string => {
    const result: { params: object; path?: string } = suggestionRouting(suggestion);
    const path = result?.path ? result.path : "/product_listing";
    if (redirect) {
      return $goto(path, result.params);
    }
    return $url(path, result.params);
  };

  const trackSuggestion = (suggestion: Suggestion): void => {
    const suggestionEntity = new SearchSuggestionEntity(suggestion);
    const entities = [suggestionEntity];
    trackClick(null, entities);
    trackProductSearchEvent();
  };

  const handleSearch = (): void => {
    trackProductSearchEvent();
    // sends the user to the all products page if they enter a blank search
    // Reset page to 1 when performing a search to avoid page parameter persistence
    const params = searchText.trim() === "" ? {} : { search: searchText, page: 1 };
    return $goto("/product_listing", params);
  };

  const handleEnterPress = (): void => {
    const suggestion = suggestions[$highlightedSuggestionIndexStore];
    if (suggestion !== undefined) {
      trackSuggestion(suggestion);
      buildHrefOrRedirect(suggestion, true);
    } else {
      handleSearch();
    }
  };

  const trackProductSearchEvent = (): void => {
    trackEvent(
      "product_search",
      new SearchEntity({
        service: "retailer_header",
        component: "search_bar",
        query: searchText,
      }),
    );
  };

  const openSuggestions = (): void => {
    isSuggestionBoxOpen = true;
  };

  const suggest = async (): Promise<{
    suggestions: Suggestion[];
    groupedSuggestions: SuggestionGroup[];
  }> => {
    let val = searchText;
    let trimVal = val && trim(val).slice(0, 50);
    if (!val || trimVal.length < 1) {
      groupedSuggestions = [];
      suggestions = [];
      return;
    } else {
      if (!isSuggestionBoxOpen) {
        openSuggestions();
      }
      const results = await getSuggestions(searchText);
      if (results.searchText === searchText) {
        suggestions = results.suggestions;
        groupedSuggestions = results.groupedSuggestions;
      }
    }
  };
  const suggestDebounced = debounce(suggest, 50);

  $: highlightIndex = $highlightedSuggestionIndexStore;
  const showSuggestionHighlight = (suggestion: Suggestion) => {
    const suggestionIndex = suggestions.indexOf(suggestion);
    return suggestionIndex === highlightIndex;
  };
  interface SubmitEvent extends Event {
    /**
     * Returns the element representing the submit button that triggered the form submission, or null if the submission was not triggered by a button.
     *
     * [MDN Reference](https://developer.mozilla.org/docs/Web/API/SubmitEvent/submitter)
     */
    readonly submitter: HTMLElement | null;
  }
  const handleFormSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    handleSearch();
  };
  const handleButtonClick = () => {
    if ($LayoutStore.isMobile) {
      searchText = "";
    }
  };

  $beforeUrlChange(() => {
    searchText = "";
    highlightedSuggestionIndexStore.reset();
    groupedSuggestions = [];
    suggestions = [];
    return true;
  });
</script>

<GlobalSearch
  data-testid="retailer_header_product_search_input"
  id="retailer_header_product_search_input"
  isDropdownOpen={isSuggestionBoxOpen}
  handleFormSubmit={e => handleFormSubmit(e)}
  handleSuggestion={suggestDebounced}
  {handleEnterPress}
  {handleButtonClick}
  {placeholder}
  {suggestions}
  bind:value={searchText}
>
  <Flex as="ul" column gap="10" testId="search-bar-suggestions">
    {#each groupedSuggestions as suggestionsGroup, index}
      <Text
        as="li"
        color="neutral"
        data-testid="group-{suggestionsGroup.header.replace(/\s/g, '-')}"
        size="xs"
        uppercase
      >
        {suggestionsGroup.header}
      </Text>
      <Flex as="ul" column gap="6">
        {#each suggestionsGroup.suggestions as suggestion}
          <NavLink
            class={showSuggestionHighlight(suggestion)
              ? "underline bg-accent-light pl-6 py-4 flex-no-shrink rounded-xs"
              : "pl-6 py-4 flex-no-shrink"}
            dynamicSize={false}
            href={buildHrefOrRedirect(suggestion)}
            tabindex={-1}
            testId="suggestion-{suggestion.field.replace('_', '-')}-{suggestion.id}"
            variant="secondary"
            on:click={() => trackSuggestion(suggestion)}
          >
            {#if suggestion.isMatched}
              <span
                >{suggestion.prefix}<strong>{suggestion.highlight}</strong>{suggestion.suffix}</span
              >
            {:else}
              {suggestion.name}
            {/if}
          </NavLink>
        {/each}
      </Flex>
    {/each}
  </Flex>
</GlobalSearch>
